import React, { useState } from 'react';
import './CS.css';
import { NavLink } from 'react-router-dom';
import { MathJax, MathJaxContext } from "better-react-mathjax";
import PageNavigator from "../../../components/PageNavigator";
import ExampleBox from "../../../components/ExampleBox";


function CS2() {
  return (
        <div className='CS'>
        <PageNavigator group="CS" />
        <p className='heading1'>Departures from Irrelevance Result</p>
        <MathJaxContext>
        <p className='heading2'>MM Assumptions Revisited</p>

        <p>
        Recall the assumptions needed for the Modigliani-Miller theorem to hold:
        </p>

        <ol>
          <li>no transaction costs,</li>
          <li>no taxes,</li>
          <li>symmetric information,</li>
          <li>competitive markets, and</li>
          <li>investors and firms borrow and lend at the same interest rates.</li>
        </ol>

        <p>
        The MM theorem demonstrates that capital structure is irrelevant in a frictionless world. Real financial markets, however, are not frictionless. Each assumption fails to some degree, and these departures from the MM assumptions create situations in which a firm's financing decisions can affect its value. Much of modern corporate finance can be understood as studying which assumptions fail and how those failures influence optimal capital structure.
        </p>


        <p className='heading2'>Taxes</p>

        <p>
        One of the largest departures from MM is the existence of taxes. In most countries, including the United States, interest payments on debt are tax deductible while dividends paid to shareholders are not. Every dollar of interest therefore reduces taxable income, creating what is known as the <strong>interest tax shield</strong>.
        </p>

        <p>
        Suppose a firm is expected to generate $100 million of earnings before interest and taxes (EBIT) every year forever. The corporate tax rate is 21%, and the firm's debt has a market value of $200 million with an interest rate of 5%.
        </p>

        <table class="minimal-table" style={{ maxWidth: "800px"}}>
        <thead>
          <tr>
            <th></th>
            <th>Unlevered Firm</th>
            <th>Levered Firm</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Annual EBIT</td>
            <td><MathJax inline>{"\\( \\$100\\)"}</MathJax> million</td>
            <td><MathJax inline>{"\\( \\$100\\)"}</MathJax> million</td>
          </tr>
          <tr>
            <td>Interest Expense</td>
            <td><MathJax inline>{"\\( \\$0\\)"}</MathJax></td>
            <td><MathJax inline>{"\\( \\$200\\times 0.05 = \\$10\\)"}</MathJax> million</td>
          </tr>
          <tr>
            <td>Taxable Income</td>
            <td><MathJax inline>{"\\( \\$100\\)"}</MathJax> million</td>
            <td><MathJax inline>{"\\( \\$100 - \\$10 = \\$90\\)"}</MathJax> million</td>
          </tr>
          <tr>
            <td>Corporate Taxes (21%)</td>
            <td><MathJax inline>{"\\( \\$100 \\times 0.21= \\$21\\)"}</MathJax> million</td>
            <td><MathJax inline>{"\\( \\$90 \\times 0.21= \\$18.9\\)"}</MathJax> million</td>
          </tr>
          <tr>
            <td><strong>Annual Tax Shield</strong></td>
            <td>—</td>
            <td><MathJax inline>{"\\( \\$21 - \\$18.9 = \\$2.1\\)"}</MathJax> million</td>
          </tr>
        </tbody>
      </table>

      <p>
      Thus, the firm saves $2.1 million in taxes each year from holding debt. How does this affect the total value of the firm? 
      </p>

      <p>
      For simplicity, assume the firm's after-tax operating cash flows are perpetuities discounted at 5% (the same rate as the firm's debt). The unlevered firm has a present value of 
      <MathJax className='math-container'>
        {`\\[
          \\begin{aligned}
          V_U = \\frac{100(1-0.21)}{0.05} = \\$ 1.58 \\text{ billion}
          \\end{aligned}
        \\]`}
      </MathJax>  
      The levered firm has a present value of 
      <MathJax className='math-container'>
        {`\\[
          \\begin{aligned}
          V_L &= \\frac{100 - \\text{taxes}}{0.05} \\\\
          &= \\frac{100 - (100 - 200(0.05))(0.21)}{0.05} \\\\
          &= \\frac{100(1-0.21)}{0.05} + \\frac{200(0.05)(0.21)}{0.05} \\\\
          &= V_U + 200(0.21) \\\\
          &= \\$ 1.622 \\text{ billion}
          \\end{aligned}
        \\]`}
      </MathJax>  
      Notice that the tax savings from debt increase the value of the firm by the total debt value (<MathJax inline>{"\\( \\$200 \\)"}</MathJax> million) 
        mulitplied by the corporate tax rate, or <MathJax inline>{"\\( \\$200 \\times 0.21 = \\$42\\)"}</MathJax> million.
      </p>

        <p>
        In general, if the interest rate on the debt equals the discount rate used to value the firm's tax shield, then the value of a levered firm is given by
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
            V_L = V_U + \\tau_c D
            \\end{aligned}
          \\]`}
        </MathJax>  
        where
        <ul>
          <li><MathJax inline>{"\\( V_U \\)"}</MathJax> is the value of the unlevered firm,</li>
          <li><MathJax inline>{"\\( \\tau_c \\)"}</MathJax> is the corporate tax rate, and</li>
          <li><MathJax inline>{"\\( D \\)"}</MathJax> is the market value of the debt.</li>
        </ul>
        </p>

        <p>
        The tax shield makes debt financing more attractive because each additional dollar of debt reduces the firm's tax burden. Holding everything else constant, taxes imply that firms should finance themselves with more debt than predicted by the original MM theorem.
        </p>

        <p>
        Of course, taxes are only one consideration. If taxes were the only market imperfection, firms would have an incentive to finance themselves almost entirely with debt. In reality, other costs increase as leverage rises.
        </p>


        <p className='heading2'>Financial Distress and Bankruptcy Costs</p>

        <p>
        The original MM theorem assumes that financial distress is costless. In reality, firms that become highly leveraged face a greater risk of financial distress and bankruptcy.
        </p>

        <p>
        Bankruptcy is expensive. Firms incur legal fees, administrative costs, disruptions to operations, and may lose customers, suppliers, or valuable employees. Even before bankruptcy occurs, the possibility of distress can reduce firm value.
        </p>

        <p>
        These expected distress costs offset some of the tax advantages of debt. 
        Thus, levered firm value is closer to the following
        <MathJax className='math-container'>
          {`\\[
            \\begin{aligned}
            V_L = V_U + \\tau_c D - \\text{present value of expected financial distress costs}
            \\end{aligned}
          \\]`}
        </MathJax>  
        </p>

        <p className='heading2'>The Trade-Off Theory</p>
        <p>
        In a world with market frictions, debt has both benefits (tax savings) and costs (financial distress). As a result, 
        firms should not choose either 0% debt (the original MM result) or 100% debt (the tax-only result). 
        Instead, they choose the amount of debt that balances these competing effects. This idea is known as the <strong>trade-off theory</strong> of capital structure.
        </p>

        <p>
        The classical trade-off theory emphasizes two opposing forces: the tax advantages of debt and the expected costs of financial distress. 
        Later theories extend this framework by considering additional market frictions, including asymmetric information, agency conflicts, and financing costs.
        </p>

        <p className='heading2'>Asymmetric Information</p>

        <p>
        MM also assumes that managers and outside investors possess the same information. In reality, managers often know more about the firm's future prospects than investors.
        </p>

        <p>
        Because issuing new equity may signal that managers believe the firm's stock is overvalued, investors often react negatively to equity offerings. Firms therefore tend to follow a <strong>pecking order</strong> when raising capital:
        </p>

        <ol>
          <li>use internally generated cash first,</li>
          <li>issue debt if external financing is needed, and</li>
          <li>issue equity only as a last resort.</li>
        </ol>

        <p>
        Under the pecking order theory, observed capital structure reflects financing choices driven by information problems rather than a single optimal debt ratio.
        </p>

        <hr />

        <p className='heading2'>Agency Problems</p>

        <p>
        Managers, shareholders, and creditors do not always have the same incentives. These conflicts, known as <strong>agency problems</strong>, can influence financing decisions.
        </p>

        <p>
        Debt can discipline managers by requiring regular interest payments, reducing the amount of free cash flow available for wasteful spending. On the other hand, excessive debt may encourage shareholders to take overly risky projects because much of the downside is borne by creditors.
        </p>

        <p>
        Capital structure therefore affects not only financing costs but also managerial incentives and investment decisions.
        </p>

        <hr />

        <p className='heading2'>Different Borrowing Rates</p>

        <p>
        The homemade leverage argument assumes that investors can borrow and lend on the same terms as corporations. In reality, firms often borrow more cheaply than individuals because they are larger, more diversified, and can pledge assets as collateral.
        </p>

        <p>
        If firms have access to financing that investors cannot easily replicate, changes in capital structure may affect investor welfare and firm value.
        </p>

        <hr />

        <p className='heading2'>Market Power and Transaction Costs</p>

        <p>
        Finally, the MM theorem assumes perfectly competitive markets with no transaction costs. Real firms incur underwriting fees, legal expenses, issuance costs, and other frictions when raising capital. Markets may also be imperfectly competitive, preventing investors from perfectly replicating corporate financing decisions.
        </p>

        <p>
        These frictions make financing choices costly and can influence when and how firms issue securities.
        </p>

        <hr />

        <p className='heading2'>Putting It All Together</p>

        <p>
        The MM theorem provides an important benchmark rather than a literal description of financial markets. It tells us that in a frictionless world, financing decisions alone cannot create value. When capital structure matters, it is because one or more MM assumptions fail.
        </p>

        <p>
        Modern theories of capital structure differ primarily in which market imperfections they emphasize. Taxes encourage firms to borrow, financial distress discourages excessive leverage, asymmetric information influences the choice between debt and equity, and agency problems shape managerial incentives. Together, these considerations help explain why firms choose different capital structures in practice.
        </p>
        </MathJaxContext>
        <PageNavigator group="CS"/>
        </div>
  );
}

export default CS2;